/**
 * FrontDesk Automations API
 * Paths use /api/accounts/automations (JWT scopes print house).
 */

import { BaseChatwootService } from "../core/base-chatwoot-service";
import type {
  Automation,
  AutomationActivityResponse,
  AutomationActivityRun,
  AutomationDetailResponse,
  AutomationListResponse,
  AutomationPayload,
  UpdateAutomationPayload,
} from "../types/automations.types";

function unwrapList(response: any): Automation[] {
  if (Array.isArray(response?.data?.payload)) return response.data.payload;
  if (Array.isArray(response?.payload)) return response.payload;
  if (Array.isArray(response)) return response as Automation[];
  return [];
}

function unwrapOne(response: any): Automation {
  if (response?.data?.payload) return response.data.payload;
  if (response?.payload) return response.payload;
  return response as Automation;
}

export class ChatwootAutomationsService extends BaseChatwootService {
  constructor(baseUrl?: string) {
    super(baseUrl);
  }

  async listAutomations(): Promise<Automation[]> {
    const response = await this.get<AutomationListResponse>(
      `/api/accounts/automations`
    );
    return unwrapList(response);
  }

  /** Recent shadow evaluations (admin audit, no message content). */
  async getActivity(): Promise<AutomationActivityRun[]> {
    const response = await this.get<AutomationActivityResponse>(
      `/api/accounts/automations/activity`
    );
    if (Array.isArray(response?.data?.payload)) return response.data.payload;
    if (Array.isArray(response?.payload)) return response.payload;
    return [];
  }

  async getAutomation(automationId: number | string): Promise<Automation> {
    const response = await this.get<AutomationDetailResponse>(
      `/api/accounts/automations/${automationId}`
    );
    return unwrapOne(response);
  }

  async createAutomation(payload: AutomationPayload): Promise<Automation> {
    const response = await this.post<AutomationDetailResponse>(
      `/api/accounts/automations`,
      { automation: payload }
    );
    return unwrapOne(response);
  }

  async updateAutomation(
    automationId: number | string,
    payload: UpdateAutomationPayload
  ): Promise<Automation> {
    const response = await this.put<AutomationDetailResponse>(
      `/api/accounts/automations/${automationId}`,
      { automation: payload }
    );
    return unwrapOne(response);
  }

  async duplicateAutomation(automationId: number | string): Promise<Automation> {
    const response = await this.post<AutomationDetailResponse>(
      `/api/accounts/automations/${automationId}/duplicate`,
      {}
    );
    return unwrapOne(response);
  }

  async reorderAutomations(
    automationIds: Array<number | string>
  ): Promise<Automation[]> {
    const response = await this.post<AutomationListResponse>(
      `/api/accounts/automations/reorder`,
      { automationIds }
    );
    return unwrapList(response);
  }

  async deleteAutomation(automationId: number | string): Promise<void> {
    await this.delete(`/api/accounts/automations/${automationId}`);
  }
}
